import React from 'react';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import { VehicleEventData } from '../../@types';
import {
  AreaChanged,
  BookingAssigned,
  CarvalooFeedback,
  DamageRecordUpdated,
  IgnitionChanged,
  ServiceNoteAdded,
  ServiceReported,
  ShockDetected,
  StatusChanged,
  TripReserved,
  Trip,
  NoteAdded,
  RecordAdded,
  TripChecked,
  BorderCrossed,
  DocumentAdded,
  MaintenanceClaim,
  RydFuelSessionStatusChanged,
  FuelSessionStarted,
  FuelSessionEnded,
} from './TimelineItemBody';
import styles from './TimelineItem.styles';
import formatDateTime from '../../utils/formatDateTime';
import ImageAdded from './ImageAdded';
import { selectSelectedTrip } from '../../redux/store/userData/trips';
import { useSelector } from 'react-redux';

const IMAGE_PATH = '/images/timeline';

type TimelineItemType =
  | 'areaChanged'
  | 'bookingAssigned'
  | 'damageRecordUpdated'
  | 'ignitionChanged'
  | 'lockChanged'
  | 'serviceNoteAdded'
  | 'serviceReported'
  | 'shockDetected'
  | 'statusChanged'
  | 'tripFinished'
  | 'tripReserved'
  | 'tripStarted';

const iconMapper = {
  areaChanged: `${IMAGE_PATH}/dot.svg`,
  bookingAssigned: `${IMAGE_PATH}/clock.svg`,
  carvalooFeedback: `${IMAGE_PATH}/camera.svg`,
  damageRecordUpdated: `${IMAGE_PATH}/redDot.svg`,
  ignitionChanged: `${IMAGE_PATH}/thunderbolt.svg`,
  lockChanged: `${IMAGE_PATH}/lock.svg`,
  serviceNoteAdded: `${IMAGE_PATH}/message.svg`,
  serviceReported: `${IMAGE_PATH}/list.svg`,
  shockDetected: `${IMAGE_PATH}/warning.svg`,
  smokeDetected: `${IMAGE_PATH}/smoking_rooms.svg`,
  borderCrossing: `${IMAGE_PATH}/dot.svg`,
  statusChanged: `${IMAGE_PATH}/dot.svg`,
  tripFinished: `${IMAGE_PATH}/lock.svg`,
  tripReserved: `${IMAGE_PATH}/clock.svg`,
  tripStarted: `${IMAGE_PATH}/unlock.svg`,
  lock: `${IMAGE_PATH}/lock.svg`,
  unlock: `${IMAGE_PATH}/unlock.svg`,
  imageAdded: `${IMAGE_PATH}/camera.svg`,
  noteAdded: `${IMAGE_PATH}/message.svg`,
  documentAdded: `${IMAGE_PATH}/clip.svg`,
  recordAdded: `${IMAGE_PATH}/message.svg`,
  checkedIn: `${IMAGE_PATH}/login.svg`,
  checkedOut: `${IMAGE_PATH}/logout.svg`,
  maintenanceClaim: `${IMAGE_PATH}/redDot.svg`,
  rydFuelSessionStatusChanged: `${IMAGE_PATH}/dot.svg`,
  fuelSessionStarted: `${IMAGE_PATH}/grey_dot.svg`,
  fuelSessionEnded: `${IMAGE_PATH}/tickGreen.svg`,
};

const rydFuelSessionIconMapper = {
  issued: `${IMAGE_PATH}/grey_dot.svg`,
  ordered: `${IMAGE_PATH}/orangeDot.svg`,
  acquired: `${IMAGE_PATH}/greenDot.svg`,
  completed: `${IMAGE_PATH}/tickGreen.svg`,
  closed: `${IMAGE_PATH}/redDot.svg`,
};

const bodyMapper = {
  areaChanged: AreaChanged,
  bookingAssigned: BookingAssigned,
  carvalooFeedback: CarvalooFeedback,
  damageRecordUpdated: DamageRecordUpdated,
  ignitionChanged: IgnitionChanged,
  lockChanged: Trip,
  borderCrossing: BorderCrossed,
  serviceNoteAdded: ServiceNoteAdded,
  serviceReported: ServiceReported,
  shockDetected: ShockDetected,
  smokeDetected: ShockDetected,
  statusChanged: StatusChanged,
  tripFinished: Trip,
  tripReserved: TripReserved,
  tripStarted: Trip,
  checkedOut: TripChecked,
  checkedIn: TripChecked,
  imageAdded: ImageAdded,
  noteAdded: NoteAdded,
  documentAdded: DocumentAdded,
  recordAdded: RecordAdded,
  maintenanceClaim: MaintenanceClaim,
  rydFuelSessionStatusChanged: RydFuelSessionStatusChanged,
  fuelSessionStarted: FuelSessionStarted,
  fuelSessionEnded: FuelSessionEnded,
};

const NotMatched = () => <p>type is not matched</p>;

type TimelineItemProps = {
  vehicleEvent: any;
  hideSecondBar?: boolean;
  hideFirstBar?: boolean;
  onClick?: () => void;
};

const TimelineItem: React.FC<TimelineItemProps> = ({
  vehicleEvent,
  hideSecondBar,
  hideFirstBar,
  onClick = () => {},
}) => {
  const intl = useIntl();
  const {
    type,
    carLocked,
    ignitionOff,
    initialTripIgnition,
    lastTripIgnition,
    addedAt,
    connectionType,
  } = vehicleEvent;
  const selectedTrip = useSelector(selectSelectedTrip);

  let updatedType = type;
  if (connectionType === 'geoTab') {
    if (type === 'tripStarted') {
      updatedType = 'checkedOut';
    } else if (type === 'tripFinished') {
      updatedType = 'checkedIn';
    }
  }
  const iconSrc =
    (['rydFuelSessionStatusChanged', 'fuelSessionEnded'].includes(
      updatedType
    ) &&
      // @ts-ignore
      rydFuelSessionIconMapper[vehicleEvent.newFuelSessionStatus]) ||
    // @ts-ignore
    iconMapper[updatedType] ||
    `${IMAGE_PATH}/dot.svg`;
  // @ts-ignore
  const Body = bodyMapper[updatedType] || NotMatched;
  const dateTime = formatDateTime(
    updatedType === 'fuelSessionEnded' && vehicleEvent.fuelledAt
      ? vehicleEvent.fuelledAt
      : addedAt
  );

  let title = intl.formatMessage({
    id: `fleetadmin.event.${updatedType}`,
  });
  if (updatedType === 'lockChanged') {
    title = intl.formatMessage({
      id: `fleetadmin.event.${carLocked ? 'unlocked' : 'locked'}`,
    });
  } else if (updatedType === 'ignitionChanged') {
    title = intl.formatMessage({
      id: `fleetadmin.event.${
        ignitionOff
          ? lastTripIgnition && selectedTrip?.status === 'DONE'
            ? 'ignitionOffFinally'
            : 'ignitionOff'
          : initialTripIgnition
          ? 'ignitionOnInitially'
          : 'ignitionOn'
      }`,
    });
  } else if (updatedType === 'rydFuelSessionStatusChanged') {
    title = intl.formatMessage({
      id: 'fleetadmin.event.rydFuelSessionStatusChanged',
      defaultMessage: 'Fuel session updated',
    });
  } else if (updatedType === 'fuelSessionStarted') {
    title = intl.formatMessage({
      id: 'fleetadmin.event.fuelSessionStarted',
      defaultMessage: 'Fuel session started',
    });
  } else if (updatedType === 'fuelSessionEnded') {
    const status = vehicleEvent.newFuelSessionStatus;
    title = `${intl.formatMessage({
      id: 'fleetadmin.event.fuelSessionEnded',
      defaultMessage: 'Fuel session ended',
    })} (${intl.formatMessage({
      id: `fleetadmin.ryd.fuelSessionStatus.${status}`,
      defaultMessage: status,
    })})`;
  } else if (updatedType === 'imageAdded') {
    title = intl.formatMessage({
      id: `fleetadmin.event.${vehicleEvent.imageType}`,
    });
  }

  const showRetentionAcceptedBadge =
    updatedType === 'tripFinished' &&
    (selectedTrip as any)?.tripEndImagesRetentionResponsibilityAccepted ===
      true;

  return (
    <>
      <div className={'timeline-item-wrapper'}>
        <div className="left">
          <div
            className={cn('first', 'bar', {
              hidden: hideFirstBar,
            })}
          />
          <div
            className="icon-wrapper"
            style={
              updatedType === 'smokeDetected'
                ? { width: 26, marginLeft: 3, marginRight: 3 }
                : {}
            }
          >
            <img src={iconSrc} alt={`${updatedType} icon`} />
          </div>
          <div
            className={cn('second', ' bar', {
              hidden: hideSecondBar,
            })}
          />
        </div>
        <div className="right">
          <p className="title">
            {title}
            <span className="date-time">{dateTime}</span>
          </p>
          <div className="body">
            <Body vehicleEvent={vehicleEvent} intl={intl} onClick={onClick} />
            {showRetentionAcceptedBadge && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '8px',
                  padding: '6px 10px',
                  borderRadius: 4,
                  fontSize: 12,
                  background: '#fff7e6',
                  color: '#ad6800',
                  border: '1px solid #ffe58f',
                  width: 'fit-content',
                }}
                data-testid="RetentionResponsibilityAcceptedBadge"
              >
                {intl.formatMessage({
                  id: 'fleetadmin.trip.tripEndImagesRetentionResponsibilityAccepted',
                  defaultMessage:
                    'All-around photos skipped — responsibility accepted',
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default TimelineItem;
